import { Fragment } from 'react';
import GuestStyleCell from './GuestStyleCell';
import { planGridRows } from '../../utils/availabilitySnapshotUtil';
import '../../css/AvailabilityGuestGrid.css';
import '../../css/AvailabilityMultiDay.css';

// Width of the empty column between two dates, in the table's scale.
const DAY_GAP = 'calc(var(--u) * 4px)';

/**
 * The hours × (date × court) table of the multi-day images: a bar per date
 * ("MONDAY 12") across that date's courts, the court names under it, then one
 * row per hour with the guest booking page's slot cells. Same sizing rules as
 * the single-day table (planGridRows), with the date×court columns standing
 * in for courts — see buildMultiDaySnapshot.
 *
 * @param {object} props
 * @param {object} props.snapshot - buildMultiDaySnapshot() output
 * @param {number} props.bodyHeight - px the hour rows may take in total
 * @param {number} props.minHourlyUnit - smallest scale (1 = guest-page size) at which hourly rows are used
 * @param {number} props.maxUnit - largest scale the type may grow to
 * @param {number} props.maxRowHeight - px; rows stop growing here on a short day
 * @param {number} props.tableWidth - px across the whole table
 * @param {number} props.maxHeaderUnit - largest scale for the header rows
 * @param {'ranges'|'hourly'} [props.rowMode] - 'hourly' forces one row per hour
 * @param {object} [props.imageSettings] - Availability images settings (font sizes in px, BOOKED colours)
 * @returns {JSX.Element}
 */
function MultiDayGridTable({ snapshot, bodyHeight, minHourlyUnit, maxUnit, maxRowHeight, tableWidth, maxHeaderUnit, rowMode, imageSettings }) {
  const { courts: columns, days } = snapshot;
  const {
    rows, merged, rowHeight, unit, cellUnit, headerUnit, cellTextBoost, openPlayTextBoost, timeTextBoost, nextDayTimeTextBoost,
  } = planGridRows(
    snapshot,
    {
      bodyHeight, minHourlyUnit, maxUnit, maxRowHeight, tableWidth, maxHeaderUnit, rowMode,
      textPx: { time: imageSettings?.timePx, cell: imageSettings?.cellPx },
    },
  );
  const tableStyle = {
    '--agg-booked-bg': imageSettings?.bookedBg || undefined,
    '--agg-booked-ink': imageSettings?.bookedText || undefined,
  };
  // Each date's courts as one block, with a narrow empty column between
  // blocks so the dates read apart. A spacer column rather than padding on
  // the first court of each date, which would make that court's cells
  // narrower than the rest and wrap their words differently.
  const dayColumns = days.map(day => columns.filter(column => column.dayIndex === day.dayIndex));
  const gridTemplateColumns = ['max-content', ...dayColumns.map((dayCols, index) => (
    `${index > 0 ? `${DAY_GAP} ` : ''}repeat(${Math.max(dayCols.length, 1)}, minmax(0, 1fr))`
  ))].join(' ');
  const gap = index => (index > 0 ? <span className="amd-day-gap" aria-hidden="true" /> : null);

  // One grid for the date bars, court names and rows, so every header sits
  // exactly over its column (see GuestStyleGridTable for why).
  return (
    <div className={`agg-table amd-table${merged ? ' agg-table-merged' : ''}`} style={tableStyle}>
      <div
        className="agg-body"
        style={{
          gridTemplateColumns,
          gridTemplateRows: 'auto auto',
          gridAutoRows: `${rowHeight}px`,
          '--u': unit, '--uc': cellUnit, '--uh': headerUnit, '--tb': cellTextBoost, '--tbo': openPlayTextBoost, '--tt': timeTextBoost,
        }}
      >
        <span className="agg-head-spacer" style={{ gridRow: '1 / span 2' }} />
        {days.map((day, index) => (
          <Fragment key={day.dateStr}>
            {gap(index)}
            <span className="amd-day-header" style={{ gridColumn: `span ${Math.max(day.courtCount, 1)}` }}>
              <span className="amd-day-name">{day.weekdayUpper}</span>
              <span className="amd-day-number">{day.dayNumber}</span>
            </span>
          </Fragment>
        ))}
        {dayColumns.map((dayCols, index) => (
          <Fragment key={days[index].dateStr}>
            {gap(index)}
            {dayCols.map(column => (
              <span key={column.number} className="amd-court-header">{column.label}</span>
            ))}
          </Fragment>
        ))}

        {rows.map(row => (
          <Fragment key={row.key}>
            <span className="agg-time-cell" style={row.isNextDay ? { '--tt': nextDayTimeTextBoost } : undefined}>
              <span className="agg-time-h">{row.timeParts.start}</span>
              <span className="agg-time-dash">{'–'}</span>
              <span className="agg-time-h">{row.timeParts.end}</span>
              <span className="agg-time-meridiem agg-time-meridiem-start">{row.timeParts.startMeridiem}</span>
              <span className="agg-time-meridiem agg-time-meridiem-end">{row.timeParts.endMeridiem}</span>
              {row.isNextDay && <span className="agg-time-nextday">{snapshot.nextDayLabel}</span>}
            </span>
            {dayColumns.map((dayCols, index) => (
              <Fragment key={days[index].dateStr}>
                {gap(index)}
                {dayCols.map(column => (
                  <div key={column.number} className="agg-cell-wrap">
                    <GuestStyleCell cell={row.cells[column.number]} />
                  </div>
                ))}
              </Fragment>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export default MultiDayGridTable;
